// ============================================================================
// Roof Designer (Stufe 1) — Reducer + Actions
// ----------------------------------------------------------------------------
// Reiner Reducer (keine Seiteneffekte). Alle Mutationen über DesignerAction.
// Kalibrierung nutzt geometry-core.metersPerPixel (deterministische Mathe).
// ============================================================================
import { metersPerPixel as computeMPerPx } from "@energyos/geometry-core";
import type { ObstacleType } from "@energyos/shared-types";
import type { DesignerState, PixelPt, ObstacleDraft } from "./types.js";

let _obstacleIdCounter = 0;
function newObstacleId(): string {
  return `obs-${++_obstacleIdCounter}`;
}

// ─── localStorage-Persistenz ─────────────────────────────────────────────────
const STORAGE_KEY_PREFIX = "energyos-roof-design-";

export function saveDesignerState(projectId: string, state: DesignerState): void {
  try {
    const key = STORAGE_KEY_PREFIX + projectId;
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Storage quota exceeded or unavailable — fail silently
  }
}

export function loadDesignerState(projectId: string): DesignerState | null {
  try {
    const key = STORAGE_KEY_PREFIX + projectId;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as DesignerState;
  } catch {
    return null;
  }
}

export function clearDesignerState(projectId: string): void {
  try {
    localStorage.removeItem(STORAGE_KEY_PREFIX + projectId);
  } catch {}
}

// ─── Action-Union ────────────────────────────────────────────────────────────
export type DesignerAction =
  | { type: "ADD_POLYGON_POINT"; point: PixelPt }
  | { type: "CLOSE_POLYGON" }
  | { type: "RESET_POLYGON" }
  | { type: "SET_CALIB_POINT"; point: PixelPt }
  | { type: "SET_CALIB_LENGTH"; realLengthM: number }
  | { type: "CONFIRM_CALIBRATION" }
  | { type: "RESET_CALIBRATION" }
  | { type: "SET_MODULE_SPEC"; patch: Partial<{ widthM: number; heightM: number; wp: number }> }
  | { type: "SET_ORIENTATION"; orientation: "portrait" | "landscape" }
  | { type: "SET_TILT"; tiltDeg: number }
  | { type: "SET_AZIMUTH"; azimuthDeg: number }
  | { type: "SET_TILT_CONFIRMED"; confirmed: boolean }
  | { type: "SET_SETBACKS"; edgeM?: number; obstacleM?: number }
  | { type: "SET_GAPS"; moduleGapM?: number; rowGapM?: number }
  | { type: "SET_OBSTACLE_DRAFT_TYPE"; obstacleType: ObstacleType }
  | { type: "SET_OBSTACLE_START"; point: PixelPt | null }
  | { type: "ADD_OBSTACLE"; obstacle: Omit<ObstacleDraft, "id"> }
  | { type: "REMOVE_OBSTACLE"; id: string }
  | { type: "SET_ACTIVE_TOOL"; tool: DesignerState["activeTool"] }
  | { type: "SET_BACKGROUND_IMAGE"; url: string | null }
  // Satelliten-Hintergrund ein-/ausblenden (GeoRef bleibt erhalten, nur Flag).
  | { type: "SET_SATELLITE_ENABLED"; enabled: boolean }
  // Kompletter State-Ersatz beim Import einer Gebäude-Dachfläche (RoofSurface →
  // RoofDesign). Der neue State kommt aus import-surface.ts (pur, deterministisch).
  | { type: "IMPORT_SURFACE_STATE"; state: DesignerState }
  // Manuelle Georeferenz: Satelliten-Hintergrund ohne importierte Dachfläche laden.
  // Zentriert den Canvas auf lat/lng, 300 m × 300 m Viewport, 0,05 m/px.
  | { type: "SET_MANUAL_GEOREF"; lat: number; lng: number };

// ─── Initial State ───────────────────────────────────────────────────────────
export function initialDesignerState(): DesignerState {
  return {
    polygon: [],
    polygonClosed: false,
    obstacles: [],
    backgroundImage: null,
    backgroundGeoRef: null,
    calibration: {
      a: null,
      b: null,
      realLengthM: 5,
      metersPerPixel: null,
      confirmed: false,
    },
    moduleSpec: { widthM: 1.134, heightM: 1.762, wp: 440 },
    orientation: "portrait",
    tiltDeg: 30,
    azimuthDeg: 180,
    tiltConfirmed: false,
    edgeSetbackM: 0.5,
    obstacleSetbackM: 0.5,
    moduleGapM: 0.02,
    rowGapM: 0.02,
    obstacleDraftType: "chimney",
    obstacleStart: null,
    activeTool: "polygon",
    importSource: null,
    canApprove: false,
    requiresHumanReview: true,
  };
}

// ─── Reducer ─────────────────────────────────────────────────────────────────
export function designerReducer(state: DesignerState, action: DesignerAction): DesignerState {
  switch (action.type) {
    case "ADD_POLYGON_POINT":
      if (state.polygonClosed) return state;
      return { ...state, polygon: [...state.polygon, action.point] };

    case "CLOSE_POLYGON":
      if (state.polygon.length < 3) return state;
      return { ...state, polygonClosed: true };

    case "RESET_POLYGON":
      return { ...state, polygon: [], polygonClosed: false };

    case "SET_CALIB_POINT": {
      const cal = state.calibration;
      if (cal.a === null) {
        return { ...state, calibration: { ...cal, a: action.point, b: null } };
      }
      return { ...state, calibration: { ...cal, b: action.point } };
    }

    case "SET_CALIB_LENGTH":
      return {
        ...state,
        calibration: { ...state.calibration, realLengthM: action.realLengthM, confirmed: false },
      };

    case "CONFIRM_CALIBRATION": {
      const { a, b, realLengthM } = state.calibration;
      if (!a || !b || !(realLengthM > 0)) return state;
      try {
        const mPerPx = computeMPerPx(a, b, realLengthM);
        return {
          ...state,
          calibration: { ...state.calibration, metersPerPixel: mPerPx, confirmed: true },
        };
      } catch {
        return state;
      }
    }

    case "RESET_CALIBRATION":
      return {
        ...state,
        calibration: { a: null, b: null, realLengthM: state.calibration.realLengthM, metersPerPixel: null, confirmed: false },
      };

    case "SET_MODULE_SPEC":
      return { ...state, moduleSpec: { ...state.moduleSpec, ...action.patch } };

    case "SET_ORIENTATION":
      return { ...state, orientation: action.orientation };

    case "SET_TILT":
      return { ...state, tiltDeg: action.tiltDeg };

    case "SET_AZIMUTH":
      return { ...state, azimuthDeg: action.azimuthDeg };

    case "SET_TILT_CONFIRMED":
      return { ...state, tiltConfirmed: action.confirmed };

    case "SET_SETBACKS":
      return {
        ...state,
        edgeSetbackM: action.edgeM ?? state.edgeSetbackM,
        obstacleSetbackM: action.obstacleM ?? state.obstacleSetbackM,
      };

    case "SET_GAPS":
      return {
        ...state,
        moduleGapM: action.moduleGapM ?? state.moduleGapM,
        rowGapM: action.rowGapM ?? state.rowGapM,
      };

    case "SET_OBSTACLE_DRAFT_TYPE":
      return { ...state, obstacleDraftType: action.obstacleType };

    case "SET_OBSTACLE_START":
      return { ...state, obstacleStart: action.point };

    case "ADD_OBSTACLE":
      return {
        ...state,
        obstacles: [...state.obstacles, { ...action.obstacle, id: newObstacleId() }],
      };

    case "REMOVE_OBSTACLE":
      return { ...state, obstacles: state.obstacles.filter((o) => o.id !== action.id) };

    case "SET_ACTIVE_TOOL":
      return { ...state, activeTool: action.tool };

    case "SET_BACKGROUND_IMAGE":
      return { ...state, backgroundImage: action.url };

    case "SET_SATELLITE_ENABLED":
      if (state.backgroundGeoRef == null) return state;
      return {
        ...state,
        backgroundGeoRef: { ...state.backgroundGeoRef, enabled: action.enabled },
      };

    case "IMPORT_SURFACE_STATE":
      // Leitplanken bleiben strukturell erhalten — egal was der Import liefert.
      return { ...action.state, canApprove: false, requiresHumanReview: true };

    case "SET_MANUAL_GEOREF":
      return {
        ...state,
        backgroundGeoRef: {
          source: "esri_world_imagery" as const,
          lat0: action.lat,
          lng0: action.lng,
          minXM: -150,
          minYM: -150,
          metersPerPixel: 0.05,
          marginPx: 0,
          enabled: true,
        },
      };

    default:
      return state;
  }
}
